import React, {
  useReducer,
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { FormattedMessage, injectIntl } from 'react-intl';
import { DropResult } from 'react-beautiful-dnd';
import AddIcon from '@mui/icons-material/Add';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import {
  ChoiceWithTextPayloadOptions,
  QuestionSingleChoiceWithText,
  QuestionMultiChoiceWithText,
  QuestionTypeV2,
} from '../../redux/types';
import SortableList from '../layout/SortableList';
import ChoiceTextQuestionOptionEditor from './ChoiceTextQuestionOptionEditor';
import SurveyOptionContext from '../../views/admin/SurveyOptionContext';

export type Props<T extends QuestionSingleChoiceWithText|QuestionMultiChoiceWithText> = {
  surveyId: number;
  sectionId?: number;
  question?: T;
  addQuestion?: (dq: Omit<T, 'id'>) => void;
  updateQuestion?: (dq: T) => void;
  onCancelEdit?: () => void;
  type: T['type'];
  disableHideAggregation?: boolean;
  enableTNA?: boolean;
} & {
  intl: {
    formatMessage: (arg: Record<string, unknown>) => string;
  }
}

type PayloadItem = ChoiceWithTextPayloadOptions;

type SortOption = PayloadItem & { name: string; };

type QuestionActions<T extends QuestionSingleChoiceWithText|QuestionMultiChoiceWithText> = { type: 'setState', payload: T } |
{ type: 'updateText'; payload: string } | {type: 'addOption'; payload: string} |
{ type: 'updateOption'; payload:ChoiceWithTextPayloadOptions } | { type: 'deleteOption', payload: ChoiceWithTextPayloadOptions; } |
{ type: 'resetState'} | { type: 'reorderOptions', payload: ChoiceWithTextPayloadOptions[] } | { type: 'hideAggregation', payload: boolean };

const initialState: { text: string, payload?: PayloadItem[], hideAggregation?: boolean } = {
  text: '',
  payload: undefined,
};

const reducer = <T extends QuestionSingleChoiceWithText|QuestionMultiChoiceWithText >(
  state: typeof initialState, action: QuestionActions<T>,
): typeof initialState => {
  switch (action.type) {
    case 'setState':
      return action.payload;
    case 'resetState':
      return initialState;
    case 'updateText':
      return {
        ...state,
        text: action.payload,
      };
    case 'addOption': {
      const newPayload = state.payload ? [...state.payload] : [];
      newPayload.push({
        id: Date.now(),
        text: action.payload,
        textBox: false,
      });
      return {
        ...state,
        payload: newPayload,
      };
    }
    case 'updateOption': {
      const newPayload = state.payload
        ? [...state.payload] : [];
      const updatedPayload = newPayload.map((e) => {
        if (e.id === action.payload.id) {
          return {
            id: e.id,
            text: action.payload.text,
            textBox: action.payload.textBox,
            placeholder: action.payload.placeholder,
            textBoxInputRequired: action.payload.textBoxInputRequired,
          };
        }
        return e;
      });
      return {
        ...state,
        payload: updatedPayload,
      };
    }
    case 'deleteOption': {
      const newPayload = state.payload ? [...state.payload] : [];
      const filteredPayload = newPayload.filter((e) => e.id !== action.payload.id);
      return {
        ...state,
        payload: filteredPayload,
      };
    }
    case 'reorderOptions': {
      return {
        ...state,
        payload: action.payload,
      };
    }
    case 'hideAggregation': {
      return {
        ...state,
        hideAggregation: action.payload,
      };
    }
    default:
      return state;
  }
};

const ChoiceTextQuestionEditor = <
  T extends QuestionSingleChoiceWithText|QuestionMultiChoiceWithText,
>({
    surveyId,
    sectionId,
    question,
    type,
    disableHideAggregation,
    enableTNA,
    intl,
    addQuestion,
    updateQuestion,
    onCancelEdit,
  }: Props<T>): JSX.Element => {
  const [state, dispatch] = useReducer(reducer<T>, initialState);
  const [optionText, setOptionText] = useState<string>('');
  const [sort, setSort] = useState<boolean>(false);
  const [tnaField, setTnaField] = useState<PayloadItem>();
  const [dynamicQuestionType, setDynamicQuestionType] = useState<T['type']>(type);
  const sortedListRef = useRef<SortOption[]>();
  const sortOptionList = state.payload?.map((e) => ({ ...e, name: e.text } as SortOption));
  const { trialHasStarted } = useContext(SurveyOptionContext);

  useEffect(() => {
    if (!question?.payload) {
      return;
    }
    const tnaVal = question.payload.find((e) => e.isTNA);
    setTnaField(tnaVal);
    const questionwithoutTnaOpts: T = {
      ...question,
      payload: question.payload.filter((v) => !v.isTNA),
    };
    dispatch({
      type: 'setState',
      payload: questionwithoutTnaOpts,
    });
  }, [question]);

  const handleQuestionTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const questionText = event.target.value;
    dispatch({
      type: 'updateText',
      payload: questionText,
    });
  };

  const onChangeOptionHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const optiontxt = event.target.value;
    setOptionText(optiontxt);
  };

  const handleEditOption = (
    item: PayloadItem,
    text: string,
    textBoxOption: {
      enableTextBox: boolean, placeholderText?: string, textBoxInputRequired?: boolean,
    },
  ) => {
    const { enableTextBox, placeholderText, textBoxInputRequired } = textBoxOption;
    dispatch({
      type: 'updateOption',
      payload: {
        id: item.id,
        text,
        textBox: enableTextBox,
        textBoxInputRequired: textBoxInputRequired || false,
        placeholder: placeholderText,
      },
    });
  };

  const handleEditTNA = (
    item: PayloadItem,
    text: string,
    textBoxOption: {
      enableTextBox: boolean, placeholderText?: string, textBoxInputRequired?: boolean,
    },
  ) => {
    const { enableTextBox, placeholderText, textBoxInputRequired } = textBoxOption;
    setTnaField({
      id: item.id,
      text,
      textBox: enableTextBox,
      placeholder: placeholderText,
      textBoxInputRequired: textBoxInputRequired || false,
      isTNA: true,
    });
  };

  const handleAddOption = () => {
    dispatch({
      type: 'addOption',
      payload: optionText,
    });
    setOptionText('');
  };

  const onEnterAddOption = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAddOption();
    }
  };

  const handleDeleteOption = (item: PayloadItem) => {
    dispatch({
      type: 'deleteOption',
      payload: item,
    });
  };

  const handleDeleteTNA = () => {
    setTnaField(undefined);
  };

  const saveMultiQuestion = () => {
    let combinedQuestion = state.payload;
    if (tnaField && combinedQuestion) {
      combinedQuestion = combinedQuestion.concat(tnaField);
    }
    if (question && updateQuestion) {
      const updatedQuestion = {
        ...question,
        type: dynamicQuestionType || type, // dynamicQuestionType takes preference
        text: state.text.trim(),
        payload: combinedQuestion?.map((e) => ({
          ...e, text: e.text.trim(), placeholder: e.placeholder?.trim(),
        })),
        hideAggregation: state.hideAggregation,
      };

      updateQuestion(updatedQuestion);
    } else if (!question && addQuestion) {
      const questionData = {
        digitalSurveyId: surveyId,
        digitalSectionId: sectionId,
        type,
        text: state.text.trim(),
        payload: combinedQuestion?.map((e) => ({
          ...e, text: e.text.trim(), placeholder: e.placeholder?.trim(),
        })),
        hideAggregation: state.hideAggregation,
      } as Omit<T, 'id'>;
      addQuestion(questionData);
    }
    if (onCancelEdit) {
      onCancelEdit();
    }
    dispatch({ type: 'resetState' });
    setTnaField(undefined);
  };

  const cancelEdit = () => {
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  const sortHandler = () => {
    setSort(true);
  };

  const saveSortHandler = () => {
    setSort(false);
    if (sortedListRef.current) {
      const payloadOpt: ChoiceWithTextPayloadOptions[] = sortedListRef.current
        .map((i) => {
          const { name, ...rest } = i;
          return rest;
        });
      dispatch({
        type: 'reorderOptions',
        payload: payloadOpt,
      });
    }
    sortedListRef.current = undefined;
  };

  const sortAscendingOrder = () => {
    if (!sortOptionList) {
      return;
    }
    const toSortedOptions = sortOptionList.sort((a, b) => {
      const nameA = a.name.toUpperCase(); // ignore upper and lowercase
      const nameB = b.name.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0; // names must be equal
    });
    sortedListRef.current = toSortedOptions;
    saveSortHandler();
    // Sorting list uses an internal state.
    // In order to get it refreshed we need to move force a new state change.
    // Therefore we force the setSort flag from false and true again.
    // The promise.resolve is a workaround to force the state change.
    Promise.resolve().then(sortHandler);
  };

  const onDragHandler = useCallback((result: DropResult, sortedList?: SortOption[]) => {
    if (sortedList) {
      sortedListRef.current = sortedList;
    }
  }, []);

  const onHideFromAggregation = (e: React.SyntheticEvent<Element, Event>, checked: boolean) => {
    dispatch({
      type: 'hideAggregation',
      payload: checked,
    });
  };

  const addTNAOption = (e: React.SyntheticEvent<Element, Event>, checked: boolean) => {
    if (checked) {
      setTnaField({
        id: Date.now(),
        text: intl.formatMessage({ id: 'questionEditor.technicallyNotEvaluable' }),
        textBox: false,
        isTNA: true,
      });
    } else {
      setTnaField(undefined);
    }
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setDynamicQuestionType(
      event.target.value as QuestionTypeV2.MultiChoiceWithText|QuestionTypeV2.SingleChoiceWithText,
    );
  };

  return (
    <Box>
      {
        updateQuestion && (
          <Box>
            <FormControl variant="outlined" size="small" sx={{ mb: 2, minWidth: 120 }}>
              <InputLabel id="demo-simple-select-standard-label">
                <FormattedMessage id="questionEditor.changeType" />
              </InputLabel>
              <Select
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                value={dynamicQuestionType || type}
                onChange={handleTypeChange}
                label={<FormattedMessage id="questionEditor.changeType" />}
                disabled={trialHasStarted}
              >
                <MenuItem value={QuestionTypeV2.SingleChoiceWithText}>
                  Single Choice With Text
                </MenuItem>
                <MenuItem value={QuestionTypeV2.MultiChoiceWithText}>
                  Multi Choice WithText
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        )
      }
      <TextField
        variant="filled"
        size="small"
        label="question text"
        value={state.text}
        onChange={handleQuestionTextChange}
        fullWidth
      />
      {
        !sort && (
          <Box aria-label="edit-container">
            {
              state.payload && (
                <Box>
                  {
                    state.payload.map((e, i) => (
                      <ChoiceTextQuestionOptionEditor
                        key={e.id}
                        optionIndex={i + 1}
                        option={e}
                        handleEditOption={handleEditOption}
                        handleDeleteOption={handleDeleteOption}
                      />
                    ))
                  }
                </Box>
              )
            }
            {
              tnaField && (
                <ChoiceTextQuestionOptionEditor
                  optionIndex={state.payload ? state.payload.length + 1 : 1}
                  option={tnaField}
                  handleEditOption={handleEditTNA}
                  handleDeleteOption={handleDeleteTNA}
                />
              )
            }
            {
              state.text && (
                <Box display="flex" sx={{ pl: 2 }}>
                  <TextField
                    variant="standard"
                    size="small"
                    label="Add Option"
                    value={optionText}
                    onChange={onChangeOptionHandler}
                    fullWidth
                    onKeyDown={onEnterAddOption}
                  />
                  <IconButton aria-label="add-option" onClick={handleAddOption} size="large">
                    <AddIcon />
                  </IconButton>
                </Box>
              )
            }
          </Box>
        )
      }
      {
          !!sort && sortOptionList && (
            <Box aria-label="sort-container">
              <SortableList<SortOption>
                droppableId="sortable-options"
                list={sortOptionList}
                onDragEndHandler={onDragHandler}
              />
            </Box>
          )
      }
      {
        enableTNA && (
          <FormControlLabel
            label={<FormattedMessage id="questionEditor.enableTNA" />}
            control={<Checkbox />}
            onChange={addTNAOption}
            checked={!!tnaField || false}
          />
        )
      }
      {
        !disableHideAggregation && (
          <FormControlLabel
            label={<FormattedMessage id="questionEditor.hideAggregation" />}
            control={<Checkbox />}
            onChange={onHideFromAggregation}
            checked={state.hideAggregation || false}
          />
        )
      }
      {
        state.payload && state.payload.length > 0 && !sort && (
          <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ mt: 2 }} gap={1}>
            <Button
              aria-label="sort-options"
              onClick={sortHandler}
              variant="outlined"
              size="small"
            >
              Reorder
            </Button>
            <Button
              aria-label="save-question"
              onClick={saveMultiQuestion}
              variant="outlined"
              size="small"
            >
              Save
            </Button>
            {
              question && (
                <Button
                  aria-label="cancel"
                  color="secondary"
                  onClick={cancelEdit}
                  variant="outlined"
                  size="small"
                >
                  Cancel
                </Button>
              )
            }
          </Box>
        )
      }
      {
        sort && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }} gap={2}>
            <Tooltip title="Sort">
              <IconButton
                aria-label="sort-asc"
                onClick={sortAscendingOrder}
              >
                <SortByAlphaIcon />
              </IconButton>
            </Tooltip>
            <Button
              aria-label="sort-options"
              onClick={saveSortHandler}
            >
              Done
            </Button>
          </Box>
        )
      }
    </Box>
  );
};

export default injectIntl(ChoiceTextQuestionEditor);
